"use client";

import Image from "next/image";
import { useCallback, type CSSProperties } from "react";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from "~/components/ui/carousel";
import { cn } from "~/lib/utils";
import { useTimedCarousel } from "../../hooks/useTimedCarousel";
import CarouselProgressDots from "../../components/CarouselProgressDots";
import { PRESS_QUOTES } from "./data";
import Lead from "~/componentsDS/Typography/Lead";
import Overline from "~/componentsDS/Typography/Overline";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { useTrackEvent } from "~/hooks/useTrackEvent";

export default function PressCarousel() {
    const sendEvent = useTrackEvent();
    const handleManualSelect = useCallback((index: number) => {
        const pressQuote = PRESS_QUOTES[index];
        if (!pressQuote) return;

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                widget: "press_features",
                meta_data: {
                    click_text: pressQuote.publication,
                    image_count: index + 1,
                    product_name: "general",
                },
            },
        });
    }, [sendEvent]);
    const {
        setApi,
        opts,
        plugins,
        selectedIndex,
        enterIndex,
        carouselRef,
        progressFillRef,
        scrollTo,
    } = useTimedCarousel({
        onManualSelect: handleManualSelect,
    });

    return (
        <article
            ref={carouselRef}
            className="group/press relative -mx-[18px] overflow-hidden md:mx-0 md:overflow-visible"
        >
            <style href="press-carousel-enter" precedence="default">{`
                @keyframes press-slide-in-right {
                    from {
                        opacity: 0;
                        transform: translateX(48px);
                    }

                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                @media (prefers-reduced-motion: no-preference) {
                    .press-enter {
                        animation: press-slide-in-right 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
                    }
                }
            `}</style>

            <Carousel
                setApi={setApi}
                loop
                desktopWheelGestures
                opts={opts}
                plugins={plugins}
            >
                <CarouselContent className="-ml-1.5 md:ml-0">
                    {PRESS_QUOTES.map((pressQuote, index) => (
                        <CarouselItem
                            key={pressQuote.publication}
                            className="basis-[calc(100%_-_48px)] pl-1.5 md:basis-full md:pl-0"
                        >
                            <div className="h-[372px] rounded-[12px] bg-pureWhite px-5 pt-6 pb-10 text-center md:h-[410px] md:bg-[#f5f5f7] md:px-20 md:py-12">
                                <div
                                    className={cn(
                                        "flex h-full flex-col items-center",
                                        index !== selectedIndex && "opacity-0 group-data-[dragging=true]/press:opacity-100",
                                        index === enterIndex && "press-enter"
                                    )}
                                >
                                    <div className="flex h-10 md:h-12 shrink-0 items-center justify-center">
                                        <Image
                                            src={pressQuote.logoUrl}
                                            alt={pressQuote.publication}
                                            width={pressQuote.logoIntrinsicWidth}
                                            height={pressQuote.logoIntrinsicHeight}
                                            sizes={`${pressQuote.logoIntrinsicWidth * 1.5}px`}
                                            loading="lazy"
                                            style={{
                                                "--mobile-logo-height": `${pressQuote.logoMobileHeight}px`,
                                                "--desktop-logo-height": `${pressQuote.logoDesktopHeight}px`,
                                            } as CSSProperties}
                                            className="h-[var(--mobile-logo-height)] w-auto object-contain md:h-[var(--desktop-logo-height)]"
                                        />
                                    </div>

                                    <Lead
                                        font="featureDisplay"
                                        size="md"
                                        className="mt-6 md:mt-8 max-lg:text-[24px]"
                                    >
                                        “{pressQuote.quote}”
                                    </Lead>

                                    <div className="pt-2 mt-auto">
                                        <Overline weight="500" className="text-gray500">
                                            Dr. Sarah Daccarett
                                        </Overline>
                                        <Paragraph size="xs" className="mt-4 text-gray400">
                                            “{pressQuote.articleTitle}”
                                        </Paragraph>
                                    </div>
                                </div>
                            </div>
                        </CarouselItem>
                    ))}
                </CarouselContent>

                <CarouselProgressDots
                    count={PRESS_QUOTES.length}
                    selectedIndex={selectedIndex}
                    progressFillRef={progressFillRef}
                    onSelect={scrollTo}
                    label="press quote"
                    className="absolute bottom-2.5 right-[34px] z-[2] bg-gray100 md:right-1/2 md:translate-x-1/2"
                    dotClassName="bg-gray200"
                />
            </Carousel>
        </article>
    );
}
